<template>
  <div class="trainer-workplaces">
    <div
      v-if="sports.length"
      class="tabs-container"
      :class="sports.length === 1 ? 'hide' : ''"
    >
      <div
        v-for="(el, idx) in sports"
        :key="el.trainer_sport_id"
        class="tab"
        :class="activeClass(idx)"
        @click="tabSw(idx)"
      >
        <img :src="activeIcon(idx, el.id)" :alt="`sport${el.id}`" />
        <p class="name">
          {{ el.value }}
        </p>
      </div>
    </div>
    <div v-if="sport" class="tabs-content">
      <div class="workplace">
        <div class="workplace-main-info">
          <div class="sport-icon-block">
            <img
              class="sport-icon"
              :src="sportIcon(sport.id)"
              alt="sport-image"
            />
            <p>{{ sport.value }}</p>
          </div>
          <div v-if="sport.experience_date" class="line-info exp">
            <span class="title">
              {{ $t('common.experience_work') }}
            </span>
            <p>
              <i class="icon-experience" />
              {{ sport.experience_date }}
              <span>{{ getAgePrefix(sport.experience_date) }}</span>
            </p>
          </div>
          <div v-if="sport.outdoor.length" class="line-info exp outdoor">
            <span class="title">
              {{ $t('common.outreach_training') }}
            </span>
            <p>
              <i class="icon-outdoor" />
              {{ $t('common.from') }}
              {{ sport.outdoor_cost }}
              <span>{{ $t(currencyVal) }}</span>
            </p>
          </div>
          <div v-if="sport.rank" class="line-info rank">
            <span class="title">
              {{ $t('common.rank') }}
            </span>
            <p>
              <i class="icon-rank" />
              <span>{{ rank }}</span>
            </p>
          </div>
        </div>
        <div v-if="clubs.length" class="workplace-clubs">
          <template v-for="(club, idx) in clubs">
            <WorkplaceClubItem
              :key="idx"
              :club="club"
              :append-to-body="appendToBody"
              :trigger="trigger"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AgePrefixMixin from '@/dependencies/mixins/profile/age-prefix'
import RanksMixin from '@/dependencies/mixins/profile/ranks'
import WorkplaceClubItem from '@/components/Trainer/parts/WorkplaceClubItem'

export default {
  name: 'Workplaces',
  components: {
    WorkplaceClubItem
  },
  mixins: [AgePrefixMixin, RanksMixin],
  props: {
    sports: {
      type: Array,
      default: () => []
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    trigger: {
      type: String,
      default: 'click'
    }
  },
  data: () => ({
    tab: 0
  }),
  computed: {
    ...mapGetters({
      getCurr: 'user/getCurrency'
    }),
    sport() {
      return this.sports?.[this.tab]
    },
    clubs() {
      return this.sport?.clubs
    },
    currencyVal() {
      return this.getCurr?.value
    },
    rank() {
      return this.ranks.find((el) => el.rank_id === this.sport.rank_id)?.value
    }
  },
  methods: {
    tabSw(_tab) {
      this.tab = _tab
    },
    activeClass(_idx) {
      return this.tab === _idx ? 'active' : ''
    },
    activeIcon(_idx, _id) {
      try {
        return require(`~/assets/img/sports/${
          this.tab === _idx ? 'orange' : 'gray'
        }/${_id}.png`)
      } catch (e) {
        return require(`~/assets/img/noclub-icon-big.jpg`)
      }
    },
    sportIcon(_id) {
      try {
        return require(`~/assets/img/sports/orange/${_id}.png`)
      } catch (e) {
        return require(`~/assets/img/noclub-icon-big.jpg`)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/css/variables';

.trainer-workplaces {
  .tabs-container {
    display: flex;
    flex-wrap: nowrap;

    .tab {
      height: 60px;
      width: 100%;
      text-align: center;
      transition: 0.2s ease;
      background-color: #f3f3f3;
      padding: 5px 0;

      &.active {
        background-color: #fff;
      }

      img {
        width: auto;
        height: 100%;
      }

      .name {
        display: none;
      }
    }
  }

  .tabs-content {
    .sport-icon-block {
      display: none;
    }

    .workplace {
      padding: 14px 10px;

      &-main-info {
        display: flex;
        flex-wrap: wrap;
      }
    }
  }
}

.workplace-main-info {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  .line-info {
    margin-bottom: 10px;

    &.exp {
      width: 48%;
    }

    &.rank {
      width: 100%;

      span {
        float: unset;
        color: #474747;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }

    span {
      font-size: 13px;
      color: $gray;

      &.title {
        line-height: 1.7;
      }
    }

    p {
      min-height: 40px;
      line-height: normal;
      font-size: 16px;
      padding: 10px;
      border-radius: 3px;
      border: solid 1px #f0f0f0;
      background-color: $gray-lighter;
      margin-bottom: 5px;

      i {
        display: none;
      }

      &:last-child {
        margin-bottom: 0;
      }

      span {
        font-size: 16px;
        color: $input-gray;
        float: right;
      }
    }
  }
}

.workplace-clubs {
  margin-top: 25px;
}

@media (min-width: 768px) {
  .trainer-workplaces {
    .tabs-container {
      margin-bottom: 20px;

      &.hide {
        display: none;
      }

      .tab {
        background-color: #fff;
        margin-right: 10px;
        border: 1px solid $input-gray;
        border-radius: 3px;
        height: 40px;
        line-height: 40px;
        padding: 0;

        .name {
          display: block;
          font-size: 15px;
          color: $gray;
        }

        &:last-child {
          margin-right: 0;
        }

        img {
          display: none;
        }

        &:hover {
          cursor: pointer;
          border: 1px solid $primary;
          transition: 0.3s ease;

          .name {
            color: $primary;
          }
        }

        &.active {
          background-color: $primary;
          border: 1px solid $primary;

          .name {
            color: #fff;
          }
        }
      }
    }

    .tabs-content {
      .workplace {
        padding: 0;
      }

      .sport-icon-block {
        text-align: center;
        display: block;
        width: 135px;
        margin-right: 15px;

        .sport-icon {
          max-height: 56px;
          max-width: 80px;
        }

        p {
          text-align: center;
          font-size: 18px;
          color: #474747;
        }
      }
    }
  }

  .workplace-main-info {
    flex-wrap: nowrap !important;
    justify-content: normal !important;

    .line-info {
      width: 28% !important;
      margin-bottom: 0 !important;
      margin-right: 12px;

      p {
        display: flex;
        align-items: center;

        i {
          color: $primary;
          display: block;
          margin-right: 5px;
        }

        span {
          color: #000;
          margin-left: 5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      &.rank {
        order: 2;
      }

      &.exp {
        &.outdoor {
          margin-right: 0;
          order: 3;
        }
      }
    }
  }

  .workplace-clubs {
    margin-top: 40px;
  }
}
</style>
